/**
 * <StepStage> — une étape entre, l'autre sort (atom).
 *
 * Le concept d'un enchaînement d'étapes, nommé une fois : un parcours qui avance d'une étape (l'envoi
 * de la copie, l'attente de la correction, une question du défi, la correction prête, les
 * félicitations) ne coupe plus sec. Quand `stepKey` change :
 *  - l'étape qui part sort en fondu, `micro` (150 ms) ;
 *  - ensuite seulement, celle qui arrive entre en fondu en montant de 12 px, `standard` (250 ms), ou
 *    `entree` (400 ms) pour une page entière (`enter="entree"`).
 * C'est le motif de référence du DS (`<Snackbar>` : fondu et 12 px, la courbe de la marque), et « une
 * chose à la fois » : les deux étapes ne sont jamais à l'écran ensemble. L'enchaînement est pur et
 * testé (`stepStageMachine.ts`).
 *
 * Pendant sa sortie, l'étape qui part garde ce qu'elle montrait (ses derniers enfants) et ne répond
 * plus au toucher. La clé ne change que d'étape à étape : tant qu'elle ne bouge pas, les enfants se
 * mettent à jour comme d'ordinaire, sans rien rejouer. Au montage, rien ne bouge : c'est la clé qui
 * CHANGE qui fait entrer.
 *
 * En mouvement réduit, la nouvelle étape est posée aussitôt, sans fondu ni montée.
 *
 * L'appelant choisit la clé : une page à étapes la tire de son étape (`step`), un parcours de son
 * rang. Deux étapes que l'élève doit lire comme une seule (une même question qui se met à jour) ont
 * la même clé.
 */
import React, { useEffect, useReducer, useRef } from "react"
import { Animated, Easing, Platform, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "../../hooks/useReduceMotion"
import { initialStepStage, stepStageReducer } from "./stepStageMachine"

/** Ce que monte l'étape qui entre : 12 px, comme `<Snackbar>`. */
export const STEP_RISE = 12

export type StepStageProps = {
    /** La clé de l'étape montrée : quand elle change, l'étape sort et la nouvelle entre. */
    stepKey: string
    children: React.ReactNode
    /** `standard` (défaut) pour une étape dans une page, `entree` pour une page entière. */
    enter?: "standard" | "entree"
    /** Remplit son parent (une page à étapes). Défaut `false` : la taille de l'étape. */
    fill?: boolean
    style?: StyleProp<ViewStyle>
    testID?: string
}

export function StepStage(props: StepStageProps) {
    const { stepKey, children, enter = "standard", fill = false, style, testID } = props
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const [state, dispatch] = useReducer(stepStageReducer, stepKey, initialStepStage)
    const opacity = useRef(new Animated.Value(1)).current
    const rise = useRef(new Animated.Value(0)).current
    const lastShown = useRef(state.shown)

    // En mouvement réduit, la clé demandée est montrée tout de suite, sans attendre l'état.
    const shown = reduceMotion ? stepKey : state.shown
    // Les enfants de l'étape montrée : pendant sa sortie, elle garde ce qu'elle montrait.
    const kept = useRef(children)
    if (stepKey === shown) kept.current = children

    useEffect(() => {
        dispatch({ type: "KEY", key: stepKey, instant: reduceMotion })
    }, [stepKey, reduceMotion])

    const { phase } = state
    const [x1, y1, x2, y2] = motion.easing
    const duration = motion[enter]
    useEffect(() => {
        const freshStep = lastShown.current !== state.shown
        lastShown.current = state.shown
        if (reduceMotion) {
            opacity.setValue(1)
            rise.setValue(0)
            return
        }
        // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
        const useNativeDriver = Platform.OS !== "web"
        const easing = Easing.bezier(x1, y1, x2, y2)
        if (phase === "leaving") {
            const leave = Animated.timing(opacity, { toValue: 0, duration: motion.micro, easing, useNativeDriver })
            leave.start(({ finished }) => finished && dispatch({ type: "LEFT" }))
            return () => leave.stop()
        }
        if (phase !== "entering") return
        // Une nouvelle étape part d'en bas ; celle qui revient pendant sa sortie repart de là où elle en est.
        if (freshStep) {
            opacity.setValue(0)
            rise.setValue(STEP_RISE)
        }
        const arrive = Animated.parallel([
            Animated.timing(opacity, { toValue: 1, duration, easing, useNativeDriver }),
            Animated.timing(rise, { toValue: 0, duration, easing, useNativeDriver }),
        ])
        arrive.start(({ finished }) => finished && dispatch({ type: "ENTERED" }))
        return () => arrive.stop()
    }, [phase, state.shown, reduceMotion, opacity, rise, duration, motion.micro, x1, y1, x2, y2])

    const leaving = !reduceMotion && phase === "leaving"
    return (
        <Animated.View
            testID={testID}
            pointerEvents={leaving ? "none" : "auto"}
            style={[fill && styles.fill, style, { opacity, transform: [{ translateY: rise }] }]}
        >
            {kept.current}
        </Animated.View>
    )
}

const styles = StyleSheet.create(() => ({
    fill: { flex: 1, minHeight: 0 },
}))
